/**
 * The sync status pill, shown on every screen.
 *
 * Offline is a NORMAL state, never an error. When the internet is down the
 * pill goes amber and says how many changes are waiting — it does not warn,
 * block, or pop a dialog. Staff keep working; the changes go up later.
 *
 * The sync engine itself arrives in phase 7. This component already reads real
 * state — the count of dirty rows and the last successful sync — so it is
 * accurate today and simply starts moving once the engine runs.
 */
import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, getSetting, SETTING_KEYS } from '../db/db';
import { formatDistanceToNow } from 'date-fns';

export function usePendingChangeCount(): number {
  return (
    useLiveQuery(async () => {
      const counts = await Promise.all([
        db.members.where('dirty').equals(1).count(),
        db.subscriptions.where('dirty').equals(1).count(),
        db.payments.where('dirty').equals(1).count(),
        db.walk_ins.where('dirty').equals(1).count(),
        db.freezes.where('dirty').equals(1).count(),
        db.price_schedule.where('dirty').equals(1).count(),
        db.coach_assignments.where('dirty').equals(1).count(),
        db.coaches.where('dirty').equals(1).count(),
        db.app_settings.where('dirty').equals(1).count(),
      ]);
      return counts.reduce((a, b) => a + b, 0);
    }, []) ?? 0
  );
}

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export default function SyncIndicator({ compact = false }: { compact?: boolean }) {
  const pending = usePendingChangeCount();
  const adapterUp = useOnlineStatus();
  const lastSynced = useLiveQuery(() => getSetting(SETTING_KEYS.lastSyncedAt), [], null);
  // The last run that FINISHED. An in-flight run says nothing yet, and reading
  // it would flip the pill from "Offline" to "Sending…" on every retry.
  const lastStatus = useLiveQuery(
    async () =>
      (await db.sync_log.orderBy('seq').reverse().filter((l) => l.status !== 'running').first())
        ?.status ?? null,
    [],
    null
  );

  // The adapter being up is not the same as having internet — see
  // isNetworkFailure in the sync engine. If the last attempt could not reach
  // the server, this is offline, and says so in amber rather than red.
  const online = adapterUp && lastStatus !== 'offline';
  const lastError = lastStatus === 'error';

  let tone: 'ok' | 'pending' | 'error';
  let label: string;

  if (lastError && online) {
    tone = 'error';
    label = 'Sync problem — see Settings';
  } else if (!online) {
    tone = 'pending';
    label = pending > 0 ? `Offline — ${pending} to send` : 'Offline — all saved';
  } else if (pending > 0) {
    tone = 'pending';
    label = `Sending ${pending}…`;
  } else {
    tone = 'ok';
    label = lastSynced
      ? `Synced ${formatDistanceToNow(new Date(lastSynced), { addSuffix: true })}`
      : 'Not synced yet';
  }

  const styles = {
    ok: 'border-status-active/40 bg-status-activeBg text-status-active',
    pending: 'border-status-expiring/40 bg-status-expiringBg text-status-expiring',
    error: 'border-status-expired/40 bg-status-expiredBg text-status-expired',
  }[tone];

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-btn border px-3 py-2 text-sm font-semibold ${styles}`}
      title={
        tone === 'pending' && !online
          ? 'Everything you do is saved on this PC and will upload automatically when the internet returns.'
          : undefined
      }
    >
      <span className="text-xs leading-none">●</span>
      {!compact && <span>{label}</span>}
    </div>
  );
}
